import { type RefObject, useLayoutEffect, useRef } from 'react';

import { useTelegram } from '@/providers/TelegramProvider';

const backgroundLocks = new Map<HTMLElement, { count: number; wasInert: boolean }>();
const scrollStyles = new Map<HTMLElement, string>();
let scrollLockCount = 0;

function lockBackground(dialog: HTMLElement): () => void {
  const background = Array.from(document.body.children).filter(
    (element): element is HTMLElement => element instanceof HTMLElement && element !== dialog,
  );
  for (const element of background) {
    const lock = backgroundLocks.get(element) ?? { count: 0, wasInert: element.inert };
    lock.count += 1;
    backgroundLocks.set(element, lock);
    element.inert = true;
  }
  if (scrollLockCount === 0) {
    for (const element of [
      document.body,
      ...document.querySelectorAll<HTMLElement>('.app-scroll'),
    ]) {
      scrollStyles.set(element, element.style.overflowY);
      element.style.overflowY = 'hidden';
    }
  }
  scrollLockCount += 1;

  return () => {
    for (const element of background) {
      const lock = backgroundLocks.get(element);
      if (!lock) continue;
      lock.count -= 1;
      if (lock.count === 0) {
        element.inert = lock.wasInert;
        backgroundLocks.delete(element);
      }
    }
    scrollLockCount -= 1;
    if (scrollLockCount === 0) {
      for (const [element, overflowY] of scrollStyles) element.style.overflowY = overflowY;
      scrollStyles.clear();
    }
  };
}

function focusableElements(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex]'),
  ).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(':disabled') &&
      !element.closest('[inert]') &&
      element.getClientRects().length > 0,
  );
}

/** Keep the sheet above page navigation, including Telegram's native BackButton. */
export function useSheetDialog(
  open: boolean,
  onClose: () => void,
  closeDisabled: boolean,
): {
  dialogRef: RefObject<HTMLDivElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
} {
  const dialogRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  const closeDisabledRef = useRef(closeDisabled);
  const { registerBackLayer } = useTelegram();

  useLayoutEffect(() => {
    closeRef.current = onClose;
    closeDisabledRef.current = closeDisabled;
  }, [onClose, closeDisabled]);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const panel = panelRef.current;
    if (!open || !dialog || !panel) return;
    const previousFocus = document.activeElement;
    const unlock = lockBackground(dialog);
    const close = (): void => {
      if (!closeDisabledRef.current) closeRef.current();
    };
    const unregisterBack = registerBackLayer(close);
    // Focus the close control, not an input: opening a sheet should not summon the phone keyboard.
    (focusableElements(panel)[0] ?? panel).focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent): void => {
      if (dialog.inert) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
      } else if (event.key === 'Tab') {
        const controls = focusableElements(panel);
        const first = controls[0] ?? panel;
        const last = controls.at(-1) ?? panel;
        const active = document.activeElement;
        if (
          !panel.contains(active) ||
          (event.shiftKey ? active === first : active === last) ||
          controls.length === 0
        ) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      unregisterBack();
      unlock();
      // Nested sheets can unmount together: wait until every background lock is released.
      queueMicrotask(() => {
        if (
          previousFocus instanceof HTMLElement &&
          previousFocus.isConnected &&
          !previousFocus.closest('[inert]')
        ) {
          previousFocus.focus({ preventScroll: true });
        }
      });
    };
  }, [open, registerBackLayer]);

  return { dialogRef, panelRef };
}
